/**
 * CalculationEngine - Handles all volume and packaging calculations
 *
 * Responsibilities:
 * - Calculate substrate and drainage volumes
 * - Calculate packaging distribution (pallets/bags)
 * - Format dimension and packaging text
 */

import { t } from './translations.js';

export default class CalculationEngine {
    /**
     * Calculate all areas and return totals with validation
     *
     * @param {Array} areas - Array of area data objects
     * @param {Function} calculatorFn - Function to calculate individual area
     * @returns {Object} Calculation results with totals and per-area data
     */
    calculateAll(areas, calculatorFn) {
        let totalSubstrate = 0;
        let totalDrainage = 0;
        const results = [];
        let hasErrors = false;

        areas.forEach(areaData => {
            const result = calculatorFn(areaData);

            if (!result.valid) {
                hasErrors = true;
            } else {
                // Only add volumes from valid areas
                totalSubstrate += result.substrateVolume;
                totalDrainage += result.drainageVolume;
            }

            results.push({
                areaId: areaData.id,
                ...result
            });
        });

        return {
            valid: !hasErrors,
            hasErrors,
            areas: results,
            totals: {
                substrateVolume: totalSubstrate,
                drainageVolume: totalDrainage
            }
        };
    }

    /**
     * Calculate packaging distribution
     *
     * @param {number} volume - Volume in liters
     * @param {number} bagSize - Bag size in liters
     * @param {number} palletSize - Pallet size in liters
     * @param {number} mediumBagSize - Medium bag size in liters (optional, for 20L bags)
     * @param {number} smallBagSize - Small bag size in liters (optional, for drainage 4L bags)
     * @returns {Object} Packaging breakdown
     */
    calculatePackaging(volume, bagSize, palletSize, mediumBagSize = null, smallBagSize = null) {
        if (volume <= 0) {
            return { totalVolume: 0, pallets: 0, bags: 0, mediumBags: 0, smallBags: 0, totalUnits: 0 };
        }

        const pallets = Math.floor(volume / palletSize);
        const remainingAfterPallets = volume % palletSize;

        let bags = 0;
        let mediumBags = 0;
        let smallBags = 0;

        // Determine packaging levels
        const hasMediumBags = mediumBagSize && mediumBagSize < bagSize;
        const hasSmallBags = smallBagSize && smallBagSize < (mediumBagSize || bagSize);

        if (hasSmallBags) {
            // Use 4-level packaging: pallets -> bags -> medium bags -> small bags
            bags = Math.floor(remainingAfterPallets / bagSize);
            const remainingAfterBags = remainingAfterPallets % bagSize;

            if (hasMediumBags) {
                mediumBags = Math.floor(remainingAfterBags / mediumBagSize);
                const remainingAfterMediumBags = remainingAfterBags % mediumBagSize;
                smallBags = Math.ceil(remainingAfterMediumBags / smallBagSize);
            } else {
                smallBags = Math.ceil(remainingAfterBags / smallBagSize);
            }

            // Calculate actual packaging volume
            const packagingVolume = (pallets * palletSize) + (bags * bagSize) + (mediumBags * mediumBagSize) + (smallBags * smallBagSize);

            return {
                totalVolume: packagingVolume,
                pallets: pallets,
                bags: bags,
                mediumBags: mediumBags,
                smallBags: smallBags,
                totalUnits: pallets + bags + mediumBags + smallBags
            };
        } else if (hasMediumBags) {
            // Use 3-level packaging: pallets -> bags -> medium bags
            bags = Math.floor(remainingAfterPallets / bagSize);
            const remainingAfterBags = remainingAfterPallets % bagSize;
            mediumBags = Math.ceil(remainingAfterBags / mediumBagSize);

            // Calculate actual packaging volume
            const packagingVolume = (pallets * palletSize) + (bags * bagSize) + (mediumBags * mediumBagSize);

            return {
                totalVolume: packagingVolume,
                pallets: pallets,
                bags: bags,
                mediumBags: mediumBags,
                smallBags: 0,
                totalUnits: pallets + bags + mediumBags
            };
        } else {
            // Use 2-level packaging: pallets -> bags
            bags = Math.ceil(remainingAfterPallets / bagSize);

            // Calculate actual packaging volume
            const packagingVolume = (pallets * palletSize) + (bags * bagSize);

            return {
                totalVolume: packagingVolume,
                pallets: pallets,
                bags: bags,
                mediumBags: 0,
                smallBags: 0,
                totalUnits: pallets + bags
            };
        }
    }

    /**
     * Calculate smart packaging - suggests optimal packaging based on available sizes
     * Prefers fewer bags when larger options are available
     *
     * @param {number} volume - Volume in liters
     * @param {Object} availableSizes - Available bag sizes { bagSize, mediumBagSize, smallBagSize, palletSize }
     * @returns {Object} Smart packaging breakdown
     */
    calculateSmartPackaging(volume, availableSizes = {}) {
        if (volume <= 0) {
            return { totalVolume: 0, pallets: 0, bags: 0, mediumBags: 0, smallBags: 0, totalUnits: 0 };
        }

        console.log(`[CalculationEngine] === calculateSmartPackaging START ===`);
        console.log(`[CalculationEngine] Input volume: ${volume}L`);
        console.log(`[CalculationEngine] availableSizes:`, availableSizes);

        const palletSize = availableSizes.palletSize || null;
        // Use bagSizes array if available, otherwise fall back to individual fields for backward compatibility
        const bagSizesArray = availableSizes.bagSizes || [];
        const bagSize = availableSizes.bagSize || null;
        const mediumBagSize = availableSizes.mediumBagSize || null;
        const smallBagSize = availableSizes.smallBagSize || null;

        // Start with pallets (only if pallet size is set)
        let pallets = 0;
        let remaining = volume;

        if (palletSize && palletSize > 0) {
            pallets = Math.floor(volume / palletSize);
            remaining = volume % palletSize;
            console.log(`[CalculationEngine] Pallets: ${pallets}x ${palletSize}L, remaining: ${remaining}L`);
        }

        let bags = 0;
        let mediumBags = 0;
        let smallBags = 0;

        // Collect all available bag sizes (excluding pallets)
        const availableBagSizes = [];

        console.log(`[CalculationEngine] bagSizesArray from PHP: [${bagSizesArray.join(', ')}]`);

        // Add sizes from the new bagSizes array
        const maxBagSize = palletSize || Infinity;
        for (const size of bagSizesArray) {
            console.log(`[CalculationEngine] Checking size ${size} < ${maxBagSize}: ${size && size < maxBagSize}`);
            if (size && size < maxBagSize) {
                availableBagSizes.push(size);
            }
        }

        // Fallback to individual fields for backward compatibility
        if (bagSize && bagSize < palletSize && !availableBagSizes.includes(bagSize)) {
            availableBagSizes.push(bagSize);
        }
        if (mediumBagSize && mediumBagSize < palletSize && !availableBagSizes.includes(mediumBagSize)) {
            availableBagSizes.push(mediumBagSize);
        }
        if (smallBagSize && smallBagSize < palletSize && !availableBagSizes.includes(smallBagSize)) {
            availableBagSizes.push(smallBagSize);
        }

        // If no bags available, use only pallets
        // But ensure we have enough volume - if there's a remainder, add one more pallet
        if (availableBagSizes.length === 0) {
            if (pallets > 0 && palletSize) {
                // If there's a remaining volume that needs to be covered, add one more pallet
                if (remaining > 0) {
                    pallets += 1;
                }
                const totalVolume = pallets * palletSize;
                return {
                    totalVolume,
                    pallets,
                    bags: 0,
                    mediumBags: 0,
                    smallBags: 0,
                    totalUnits: pallets
                };
            }
            // If we have neither pallets nor bags, return zero
            return {
                totalVolume: 0,
                pallets: 0,
                bags: 0,
                mediumBags: 0,
                smallBags: 0,
                totalUnits: 0
            };
        }

        // Sort in descending order (largest first) for optimal packaging
        availableBagSizes.sort((a, b) => b - a);
        console.log(`[CalculationEngine] Available bag sizes (sorted): [${availableBagSizes.join(', ')}]`);

        // Map sizes to category for backward compatibility
        const getSizeCategory = (size) => {
            if (size >= 50) return 'bags';
            if (size >= 15) return 'mediumBags';  // Changed from 20 to 15 for 15L bags
            return 'smallBags';
        };

        // Track actual bag sizes used (for product lookup)
        let usedBagSize = 0;
        let usedMediumBagSize = 0;
        let usedSmallBagSize = 0;

        // Track ACTUAL volume of bags in each category (not just count * last size)
        // This is needed when we use bags of different sizes in the same category
        let bagsVolume = 0;
        let mediumBagsVolume = 0;
        let smallBagsVolume = 0;

        // Track bags by exact size for proper display (e.g., 1x 10L + 2x 2L instead of 3x 2L)
        const bagsBySize = new Map(); // size -> count

        // Helper to add bags to the map
        const addBagsToMap = (size, count) => {
            if (count > 0) {
                bagsBySize.set(size, (bagsBySize.get(size) || 0) + count);
            }
        };

        // Multi-level packaging: use largest reasonable bag first, then fill remainder with smaller bags
        // For remainders, prefer FEWER bags over less waste (e.g., 1x10L instead of 2x5L, but 2x2L instead of 1x5L if it gives less waste)
        console.log(`[CalculationEngine] Starting multi-level packaging for ${remaining}L`);

        for (const size of availableBagSizes) {
            if (remaining <= 0) break;

            const bagCount = Math.floor(remaining / size);

            if (bagCount > 0) {
                // Use as many of this size as we can without going over
                const category = getSizeCategory(size);
                const addedVolume = bagCount * size;
                if (category === 'bags') {
                    bags += bagCount;
                    usedBagSize = size;
                    bagsVolume += addedVolume;
                    addBagsToMap(size, bagCount);
                } else if (category === 'mediumBags') {
                    mediumBags += bagCount;
                    usedMediumBagSize = size;
                    mediumBagsVolume += addedVolume;
                    addBagsToMap(size, bagCount);
                } else if (category === 'smallBags') {
                    smallBags += bagCount;
                    usedSmallBagSize = size;
                    smallBagsVolume += addedVolume;
                    addBagsToMap(size, bagCount);
                }
                remaining -= bagCount * size;
                console.log(`[CalculationEngine] Used ${bagCount}x ${size}L (category: ${category}, total ${category}Volume: ${category === 'bags' ? bagsVolume : category === 'mediumBags' ? mediumBagsVolume : smallBagsVolume}L), remaining: ${remaining}L`);
            }
        }

        // For any remaining volume, find the optimal packaging using ALL available sizes
        // Priority: 1) Minimize waste (prefer 0% waste), 2) Minimize bag count
        if (remaining > 0 && availableBagSizes.length > 0) {
            console.log(`[CalculationEngine] Finding optimal packaging for ${remaining}L remainder`);

            let bestOption = null;
            let bestScore = Infinity;

            // Try using 1, 2, or 3 of each available size
            for (const size of availableBagSizes) {
                for (let count = 1; count <= 3; count++) {
                    const totalSize = count * size;
                    if (totalSize >= remaining) {
                        const waste = ((totalSize - remaining) / totalSize) * 100;
                        // Only consider if waste is reasonable (under 50%)
                        if (waste < 50) {
                            // Score: prioritize 0% waste, then bag count, then less waste
                            // 0% waste = score < 1000 (no penalty)
                            // With waste = score >= 1000 (waste penalty * 1000) + bag count
                            let score;
                            if (waste === 0) {
                                score = count * 10; // Prefer fewer bags when no waste
                            } else {
                                score = (waste * 1000) + count; // Heavy penalty for waste
                            }

                            console.log(`[CalculationEngine] Option: ${count}x ${size}L = ${totalSize}L (waste: ${waste.toFixed(1)}%, score: ${score})`);

                            if (score < bestScore) {
                                bestScore = score;
                                bestOption = { size, count, waste, method: 'multi' };
                            }
                        }
                    }
                }
            }

            // Apply the best option
            if (bestOption) {
                const category = getSizeCategory(bestOption.size);
                const addedVolume = bestOption.count * bestOption.size;
                if (category === 'bags') {
                    bags += bestOption.count;
                    usedBagSize = usedBagSize || bestOption.size;
                    bagsVolume += addedVolume;
                    addBagsToMap(bestOption.size, bestOption.count);
                } else if (category === 'mediumBags') {
                    mediumBags += bestOption.count;
                    usedMediumBagSize = usedMediumBagSize || bestOption.size;
                    mediumBagsVolume += addedVolume;
                    addBagsToMap(bestOption.size, bestOption.count);
                } else if (category === 'smallBags') {
                    smallBags += bestOption.count;
                    usedSmallBagSize = usedSmallBagSize || bestOption.size;
                    smallBagsVolume += addedVolume;
                    addBagsToMap(bestOption.size, bestOption.count);
                }
                console.log(`[CalculationEngine] Best option for ${remaining}L: ${bestOption.count}x ${bestOption.size}L (${bestOption.waste.toFixed(1)}% waste, score: ${bestScore})`);
                remaining = 0;
            }
        }

        // If we still have remaining volume and didn't use the smallest bag, use it
        if (remaining > 0 && availableBagSizes.length > 0) {
            const smallestSize = availableBagSizes[availableBagSizes.length - 1];
            const bagCount = Math.ceil(remaining / smallestSize);
            const category = getSizeCategory(smallestSize);
            const addedVolume = bagCount * smallestSize;
            console.log(`[CalculationEngine] Final fallback: ${bagCount}x ${smallestSize}L for ${remaining}L remainder (category: ${category})`);
            if (category === 'bags') {
                bags += bagCount;
                usedBagSize = usedBagSize || smallestSize;
                bagsVolume += addedVolume;
                addBagsToMap(smallestSize, bagCount);
            } else if (category === 'mediumBags') {
                mediumBags += bagCount;
                usedMediumBagSize = usedMediumBagSize || smallestSize;
                mediumBagsVolume += addedVolume;
                addBagsToMap(smallestSize, bagCount);
            } else if (category === 'smallBags') {
                smallBags += bagCount;
                usedSmallBagSize = usedSmallBagSize || smallestSize;
                smallBagsVolume += addedVolume;
                addBagsToMap(smallestSize, bagCount);
            }
            remaining = 0;
        }

        console.log(`[CalculationEngine] Final breakdown: ${bags}x bags (${usedBagSize}L), ${mediumBags}x medium (${usedMediumBagSize}L), ${smallBags}x small (${usedSmallBagSize}L)`);
        console.log(`[CalculationEngine] Actual volumes: ${bagsVolume}L bags, ${mediumBagsVolume}L medium, ${smallBagsVolume}L small`);

        // Calculate total volume using actual bag sizes used
        // Use the tracked *Volume variables instead of count * lastSize to handle mixed bag sizes
        const palletVolume = (palletSize && pallets > 0) ? (pallets * palletSize) : 0;
        let totalVolume = palletVolume + bagsVolume + mediumBagsVolume + smallBagsVolume;
        console.log(`[CalculationEngine] Calculated totalVolume: ${palletVolume} + ${bagsVolume} + ${mediumBagsVolume} + ${smallBagsVolume} = ${totalVolume}L`);

        // Final safety check: ensure we have at least the required volume
        // If we're short and have pallets available, add one more pallet
        if (totalVolume < volume && palletSize && palletSize > 0) {
            pallets += 1;
            totalVolume += palletSize;
        }

        // Convert bagsBySize Map to plain object for return
        const bagsBySizeObj = Object.fromEntries(bagsBySize);
        console.log(`[CalculationEngine] Bags by size:`, bagsBySizeObj);

        return {
            totalVolume,
            pallets,
            bags,
            mediumBags,
            smallBags,
            totalUnits: pallets + bags + mediumBags + smallBags,
            // Include the actual bag sizes used for product lookup
            usedBagSizes: {
                bag: usedBagSize,
                mediumBag: usedMediumBagSize,
                smallBag: usedSmallBagSize,
                pallet: palletSize
            },
            // Include detailed breakdown by size for proper display (e.g., "1x 10L + 2x 2L" instead of "3x 2L")
            bagsBySize: bagsBySizeObj
        };
    }

    /**
     * Calculate products with packaging and safety buffer
     *
     * @param {Object} data - Calculation results data
     * @param {number} safetyBufferPercent - Safety buffer percentage (default 20)
     * @param {boolean} roundTo100 - Whether to round to nearest 100 (default true)
     * @param {Object} productSizes - Available product sizes
     * @param {Array} availableBagSizes - Array of available bag sizes with availability info
     * @returns {Object} Products with packaging
     */
    calculateProducts(data, safetyBufferPercent = 20, roundTo100 = true, productSizes = {}, availableBagSizes = null) {
        const SAFETY_MULTIPLIER = 1 + (safetyBufferPercent / 100);
        const BAG_SIZE = 50; // liters (default, can be overridden by productSizes)
        const PALLET_SIZE = 1200; // liters (default, can be overridden by productSizes)
        const BIG_BAG_SIZE = 2000; // liters (default, can be overridden by productSizes)

        // Helper function to round up to nearest 100
        const roundUpToNearest100 = (value) => {
            return Math.ceil(value / 100) * 100;
        };

        // Calculate with safety margin (round to reasonable precision)
        // Safety buffer applies ONLY to substrate, NOT to drainage
        const substrateVolume = data.totals.substrateVolume * SAFETY_MULTIPLIER;
        const drainageVolume = data.totals.drainageVolume;  // No safety buffer for drainage

        // Round up to nearest 100 if requested (for packaging calculation)
        // Skip rounding for small volumes (< 100L) to avoid over-packaging
        let packagingSubstrate = substrateVolume;
        let packagingDrainage = drainageVolume;

        if (roundTo100) {
            // Only round to nearest 100 for volumes >= 100L
            // For smaller volumes, use ceiling to nearest whole number instead
            packagingSubstrate = substrateVolume >= 100 ? roundUpToNearest100(substrateVolume) : Math.ceil(substrateVolume);
            packagingDrainage = drainageVolume >= 100 ? roundUpToNearest100(drainageVolume) : Math.ceil(drainageVolume);
            console.log(`[CalculationEngine] Rounding: substrate ${substrateVolume.toFixed(1)}L -> ${packagingSubstrate}L, drainage ${drainageVolume.toFixed(1)}L -> ${packagingDrainage}L`);
        } else {
            // Always round up to ensure we have enough material
            packagingSubstrate = Math.ceil(substrateVolume);
            packagingDrainage = Math.ceil(drainageVolume);
        }

        // Get actual product sizes from productSizes parameter
        const substrateBagSize = productSizes.substrateBagSize || BAG_SIZE;
        const substrateMediumBagSize = productSizes.substrateMediumBagSize || null;
        const substrateSmallBagSize = productSizes.substrateSmallBagSize || null;
        // IMPORTANT: Do NOT use fallback for pallet size - if null, it means no pallet is available
        const substratePalletSize = productSizes.substratePalletSize; // Can be null!
        const drainageBagSize = productSizes.drainageBagSize || BAG_SIZE;
        const drainageSmallBagSize = productSizes.drainageSmallBagSize || null;
        const drainageBigBagSize = productSizes.drainageBigBagSize || BIG_BAG_SIZE;

        // Build available sizes map for smart packaging
        const availableSizes = {
            palletSize: substratePalletSize,
            bagSizes: [] // Array of ALL available bag sizes (not limited to 3)
        };

        // Collect ALL available bag sizes from availableBagSizes
        if (availableBagSizes) {
            console.log(`[CalculationEngine] Processing availableBagSizes with ${availableBagSizes.length} items:`, availableBagSizes.map(s => `${s.size}L (available: ${s.available}, type: ${s.type})`));
            for (const sizeInfo of availableBagSizes) {
                if (sizeInfo.available && sizeInfo.type !== 'pallet') {
                    // Collect all bag sizes regardless of type
                    availableSizes.bagSizes.push(sizeInfo.size);
                }
                if (sizeInfo.type === 'pallet' && sizeInfo.available) {
                    availableSizes.palletSize = sizeInfo.size;
                }
            }
        } else {
            // Fallback to productSizes if availableBagSizes not provided
            console.log(`[CalculationEngine] availableBagSizes not provided, using fallback productSizes:`, { substrateBagSize, substrateMediumBagSize, substrateSmallBagSize });
            if (substrateBagSize) availableSizes.bagSizes.push(substrateBagSize);
            if (substrateMediumBagSize) availableSizes.bagSizes.push(substrateMediumBagSize);
            if (substrateSmallBagSize) availableSizes.bagSizes.push(substrateSmallBagSize);
        }

        // Sort bag sizes in descending order (largest first)
        availableSizes.bagSizes.sort((a, b) => b - a);
        console.log(`[CalculationEngine] Final availableSizes.bagSizes: [${availableSizes.bagSizes.join(', ')}]`);

        // Use smart packaging for substrate (suggests optimal packaging based on ACTUALLY available sizes)
        const substratePackaging = this.calculateSmartPackaging(packagingSubstrate, availableSizes);

        // Drainage uses standard packaging hierarchy
        const drainagePackaging = this.calculatePackaging(packagingDrainage, drainageBagSize, drainageBigBagSize, null, drainageSmallBagSize);

        // IMPORTANT: Display the ORIGINAL volume (what user needs), not the packaging volume (which includes safety buffer)
        // The packaging volume might be higher (e.g., 30L from 6x5L bags) when user needs less (e.g., 25L)
        substratePackaging.displayVolume = data.totals.substrateVolume;
        drainagePackaging.displayVolume = data.totals.drainageVolume;

        return {
            substrate: substratePackaging,
            drainage: drainagePackaging,
            safetyBuffer: safetyBufferPercent,
            totalVolume: substrateVolume + drainageVolume,
            originalVolumes: {
                substrate: data.totals.substrateVolume,
                drainage: data.totals.drainageVolume
            },
            productSizes: {
                substrateBagSize,
                substrateMediumBagSize,
                substrateSmallBagSize,
                substratePalletSize,
                drainageBagSize,
                drainageSmallBagSize,
                drainageBigBagSize
            }
        };
    }

    /**
     * Format packaging breakdown text
     *
     * @param {Object} packaging - Packaging object with pallets, bags, mediumBags, smallBags, totalVolume
     * @param {string} materialName - Material name (unused, kept for compatibility)
     * @param {string} type - Product type ('substrate' or 'drainage')
     * @param {Object} productSizes - Product sizes object with bagSize, mediumBagSize, smallBagSize, palletSize, bigBagSize
     * @returns {string} Formatted breakdown HTML
     */
    formatPackagingBreakdown(packaging, materialName, type, productSizes = {}) {
        const parts = [];

        // Get actual bag sizes used (from smart packaging) or fall back to productSizes
        const usedBagSizes = packaging.usedBagSizes || {};

        if (type === 'drainage') {
            // Drainage uses big bags (size can be 1500L, 2000L, etc.)
            const bigBagSize = usedBagSizes.pallet || productSizes.bigBagSize || 2000;
            if (packaging.pallets > 0) {
                const translationKey = bigBagSize === 1500 ? 'big_bag_1500_l' : 'big_bag_2000_l';
                parts.push(`${packaging.pallets} x ${t(translationKey)}`);
            }
        } else {
            // Substrate uses pallets
            const palletSize = usedBagSizes.pallet || productSizes.palletSize || 1200;
            if (packaging.pallets > 0) {
                const translationKey = palletSize === 1500 ? 'big_bag_1500_l' : 'pallet_1200_l';
                parts.push(`${packaging.pallets} x ${t(translationKey)}`);
            }
        }

        // Check if we have bagsBySize (detailed breakdown) or fall back to old method
        if (packaging.bagsBySize && Object.keys(packaging.bagsBySize).length > 0) {
            // New method: use bagsBySize for accurate display (e.g., 1x 10L + 2x 2L)
            // This includes ALL bags regardless of category (bags, mediumBags, smallBags)
            // Sort by size descending (largest first)
            const sortedSizes = Object.entries(packaging.bagsBySize).sort((a, b) => parseInt(b[0]) - parseInt(a[0]));
            for (const [sizeStr, count] of sortedSizes) {
                const bagSize = parseInt(sizeStr);
                let translationKey;
                if (bagSize === 1) {
                    translationKey = 'bag_1_l';
                } else if (bagSize === 2) {
                    translationKey = 'bag_2_l';
                } else if (bagSize === 4) {
                    translationKey = 'bag_4_l';
                } else if (bagSize === 5) {
                    translationKey = 'bag_5_l';
                } else if (bagSize === 10) {
                    translationKey = 'bag_10_l';
                } else if (bagSize === 15) {
                    translationKey = 'bag_15_l';
                } else if (bagSize === 50) {
                    translationKey = 'bag_50_l';
                } else {
                    translationKey = `bag_${bagSize}_l`;
                }
                parts.push(`${count}x ${t(translationKey)}`);
            }
        } else {
            // Old method: use bags/mediumBags/smallBags counts (for drainage or when bagsBySize not available)
            if (packaging.bags > 0) {
                const bagSize = usedBagSizes.bag || productSizes.bagSize || 50;
                const translationKey = bagSize === 1500 ? 'big_bag_1500_l' : 'bag_50_l';
                parts.push(`${packaging.bags}x ${t(translationKey)}`);
            }

            // Medium bags (20L) for substrate
            if (packaging.mediumBags > 0) {
                const mediumBagSize = usedBagSizes.mediumBag || productSizes.mediumBagSize || 20;
                const translationKey = mediumBagSize === 20 ? 'bag_20_l' : `bag_${mediumBagSize}_l`;
                parts.push(`${packaging.mediumBags}x ${t(translationKey)}`);
            }

            // Small bags
            if (packaging.smallBags > 0) {
                const smallBagSize = usedBagSizes.smallBag || productSizes.smallBagSize || (type === 'drainage' ? 4 : 5);
                let translationKey;
                if (smallBagSize === 5) {
                    translationKey = 'bag_5_l';
                } else if (smallBagSize === 4) {
                    translationKey = 'bag_4_l';
                } else if (smallBagSize === 10) {
                    translationKey = 'bag_10_l';
                } else if (smallBagSize === 15) {
                    translationKey = 'bag_15_l';
                } else {
                    translationKey = `bag_${smallBagSize}_l`;
                }
                parts.push(`${packaging.smallBags}x ${t(translationKey)}`);
            }
        }

        const breakdown = parts.length > 0 ? ` (${parts.join(' + ')})` : '';
        // Show totalVolume (actual packaging volume with bags), not displayVolume (original user need)
        // This ensures the volume matches the breakdown (e.g., "25 L (1x 20l + 1x 5l)")
        const displayVol = packaging.totalVolume;
        const volumeText = Math.ceil(displayVol).toLocaleString('pl-PL');
        const materialLabel = type === 'drainage' ? t('drainage_expanded_clay') : t('substrate_base');

        return `${materialLabel}: <span class="font-bold">${volumeText} L</span><span class="text-gray-700">${breakdown}</span>`;
    }

    /**
     * Format dimensions text for display
     *
     * @param {Object} area - Area data with dimensions and shape type
     * @returns {string} Formatted dimensions text
     */
    formatDimensionsText(area) {
        const { dimensions, shapeType, calculationMethod } = area;

        if (!dimensions) return '-';

        // If user knew the volume
        if (calculationMethod === 'known' || shapeType === 'custom') {
            const volume = dimensions.volume || 0;
            return t('volume_text', volume);
        }

        // Box dimensions
        if (shapeType === 'box') {
            const z = dimensions.z || 0; // depth/height
            const x = dimensions.x || 0; // length
            const y = dimensions.y || 0; // width
            return t('box_dimensions_text').replace('%scm', `${z}cm`).replace('%scm', `${x}cm`).replace('%scm', `${y}cm`);
        }

        // Pot dimensions
        if (shapeType === 'pot') {
            const d = dimensions.d || 0; // diameter
            const h = dimensions.h || 0; // height
            return t('pot_dimensions_text').replace('%scm', `${d}cm`).replace('%scm', `${h}cm`);
        }

        // Ground dimensions
        if (shapeType === 'ground') {
            const areaValue = dimensions.area || 0; // m²
            const depth = dimensions.depth || 0; // cm
            return t('ground_dimensions_text').replace('%s', areaValue).replace('%s', depth);
        }

        return '-';
    }
}
